import '@awesome.me/webawesome/dist/components/button/button.js';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConversationTurn, ExtractedItem } from '../../services/extraction.js';
import { extractItems } from '../../services/extraction.js';

interface Message {
  role: 'system' | 'user';
  text: string;
}

interface ConversationalCaptureProps {
  onExtracted: (items: ExtractedItem[]) => void;
  isProcessing: boolean;
  setProcessing: (v: boolean) => void;
}

export function ConversationalCapture({
  onExtracted,
  isProcessing,
  setProcessing,
}: ConversationalCaptureProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([
    { role: 'system', text: t('onboarding:conversational.systemStart') },
  ]);
  const [input, setInput] = useState('');
  const [allExtracted, setAllExtracted] = useState<ExtractedItem[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const send = () => {
    if (input.trim().length === 0 || isProcessing) return;
    const userText = input.trim();
    const updatedMessages: Message[] = [...messages, { role: 'user', text: userText }];
    setMessages(updatedMessages);
    setInput('');
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    // Send to AI for extraction with conversation history
    setProcessing(true);
    const history: ConversationTurn[] = messages.map(m => ({
      role: m.role,
      content: m.text,
    }));
    void extractItems({
      mode: 'conversational',
      input: userText,
      conversationHistory: history,
    })
      .then(result => {
        setAllExtracted(prev => [...prev, ...result.items]);
        if (result.done) {
          onExtracted([...allExtracted, ...result.items]);
        } else if (result.nextQuestion) {
          setMessages(prev => [...prev, { role: 'system', text: result.nextQuestion as string }]);
        }
        setProcessing(false);
      })
      .catch(() => {
        setProcessing(false);
      });
  };

  const handleDone = () => {
    onExtracted(allExtracted);
  };

  return (
    <div style={{ maxWidth: '480px', width: '100%', margin: '0 auto', padding: 'var(--space-xl)' }}>
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.5rem',
          marginBottom: 'var(--space-lg)',
        }}
      >
        {t('onboarding:conversational.title')}
      </h2>

      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          marginBottom: 'var(--space-md)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-sm)',
        }}
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              padding: 'var(--space-sm) var(--space-md)',
              borderRadius: 'var(--radius-md)',
              background: msg.role === 'system' ? 'var(--color-card)' : 'var(--color-positive)',
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              fontSize: '0.9rem',
              color:
                msg.role === 'user' ? 'var(--color-positive-text)' : 'var(--color-text-primary)',
            }}
          >
            {msg.text}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
        <input
          value={input}
          onChange={e => {
            setInput(e.target.value);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') send();
          }}
          placeholder={t('onboarding:conversational.placeholder')}
          disabled={isProcessing}
          style={{
            flex: 1,
            background: 'var(--color-card)',
            border: '1px solid var(--color-card-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-sm) var(--space-md)',
            color: 'var(--color-text-primary)',
            fontSize: '0.9rem',
          }}
        />
        <wa-button
          variant="neutral"
          onClick={send}
          disabled={isProcessing || input.trim().length === 0}
        >
          {t('onboarding:conversational.send')}
        </wa-button>
      </div>

      <div style={{ marginTop: 'var(--space-lg)', textAlign: 'right' }}>
        <wa-button
          variant="brand"
          onClick={handleDone}
          disabled={isProcessing || messages.length < 2}
        >
          {isProcessing ? t('capture:quickAdd.processing') : t('onboarding:conversational.done')}
        </wa-button>
      </div>
    </div>
  );
}
